import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { FaChevronDown, FaLightbulb } from 'react-icons/fa'
import { cloneElement } from 'react'
import { usePathname } from 'next/navigation'

const NavItem = ({ data, overLight = true, scrolled = false }) => {
    const [hoveredNavItem, setHoveredNavItem] = useState('')
    const [activeProduct, setActiveProduct] = useState(0)
    const pathName = usePathname()

    // Redirect mappings: original href -> actual destination
    const redirectMap = {
        '/services/ai-services': '/services/ai-as-a-service',
        '/ai-consultant-services': '/services/ai-consulting-services',
    }

    // Helper function to normalize paths (remove trailing slashes, query params)
    const normalizePath = (path) => {
        if (!path) return ''
        // Remove trailing slash (except for root)
        let normalized = path === '/' ? path : path.replace(/\/$/, '')
        // Remove query parameters and hash
        normalized = normalized.split('?')[0].split('#')[0]
        return normalized
    }

    // Helper function to check if a route is active
    const isActiveRoute = (href) => {
        if (!href) return false

        const normalizedHref = normalizePath(href)
        const normalizedPathName = normalizePath(pathName)

        // Handle exact matches
        if (normalizedPathName === normalizedHref) return true

        // Check redirect destination
        const redirectDestination = redirectMap[normalizedHref]
        if (redirectDestination && normalizedPathName === normalizePath(redirectDestination)) {
            return true
        }

        // Handle parent routes (e.g., /services matches /services/ai-services)
        if (normalizedHref !== '/' && normalizedPathName.startsWith(normalizedHref + '/')) {
            return true
        }

        return false
    }

    // Check if current nav item is active
    const isNavItemActive = isActiveRoute(data.href)

    // Check if we're on the exact route (not a child route)
    const isExactRoute = () => {
        const normalizedHref = normalizePath(data.href)
        const normalizedPathName = normalizePath(pathName)
        return normalizedPathName === normalizedHref
    }

    // Check if any dropdown item is active (for dropdown menus)
    const hasActiveDropdownItem =
        data.isDropdown &&
        data.type === 'link' &&
        data.dropdown?.some((section) =>
            section.sub_section_items?.some((item) => isActiveRoute(item.href)),
        )

    // Check if any product link is active
    const hasActiveProduct =
        data.isDropdown &&
        data.type === 'images' &&
        data.dropdown?.links?.some((link) => isActiveRoute(link.href))

    // Check if any simple-dropdown link is active (e.g. the Company menu)
    const hasActiveSimpleItem =
        data.isDropdown &&
        data.type === 'simple' &&
        data.dropdown?.some((item) => isActiveRoute(item.href))

    /* ---------------------------------------------------------------------- *
     *  Theme tokens — the dropdowns inherit the floating pill's light/dark
     *  glass look so they read as one continuous surface with the navbar.
     * ---------------------------------------------------------------------- */
    const isActive =
        isNavItemActive || hasActiveDropdownItem || hasActiveProduct || hasActiveSimpleItem

    // Trigger pill (label + chevron)
    const triggerBase =
        'group/nav inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-2 text-sm font-medium transition-colors duration-200'
    const triggerColors = isActive
        ? overLight
            ? 'text-[#058BF0] bg-[#058BF0]/[0.08]'
            : 'text-white bg-white/[0.08]'
        : overLight
        ? 'text-[#0a1b3a]/85 hover:text-[#058BF0] hover:bg-[#0a1b3a]/[0.05]'
        : 'text-white/80 hover:text-white hover:bg-white/[0.07]'

    // Glass dropdown panel — solid, opaque background so content behind the page
    // never bleeds through and text keeps full contrast.
    const panelBg = overLight
        ? 'border border-[#0a1b3a]/10 bg-white shadow-2xl shadow-black/15'
        : 'border border-white/10 bg-[#0b1222] shadow-2xl shadow-black/60'
    const sectionLabel = overLight ? 'text-[#0a1b3a]/50' : 'text-white/50'
    const itemHover = overLight ? 'group-hover:bg-[#058BF0]/[0.07]' : 'group-hover:bg-white/[0.06]'
    const itemHeading = overLight
        ? 'text-[#0a1b3a]/90 group-hover:text-[#058BF0]'
        : 'text-white group-hover:text-white'
    const itemSub = overLight
        ? 'text-[#0a1b3a]/55 group-hover:text-[#0a1b3a]/70'
        : 'text-white/60 group-hover:text-white/80'
    const iconChip = overLight
        ? 'border-[#0a1b3a]/10 bg-white text-[#058BF0] group-hover:border-[#058BF0] group-hover:bg-[#058BF0] group-hover:text-white'
        : 'border-white/10 bg-white/[0.06] text-[#5BB8FF] group-hover:border-[#058BF0] group-hover:bg-[#058BF0] group-hover:text-white'
    const activeItemBg = overLight ? 'bg-[#058BF0]/10' : 'bg-[#058BF0]/15'
    const activeHeading = overLight ? 'text-[#058BF0]' : 'text-white'

    // Dropdown vertical anchor — sits just under the pill (4px gap) and follows
    // it as it docks to the top edge on scroll.
    const dropTop = scrolled ? 'top-[68px]' : 'top-[84px]'

    // Invisible hover bridge: spans the dead space between the trigger and the
    // panel so moving the mouse down doesn't fire mouseLeave and close the menu.
    // It's a descendant of the hovered nav item, which is what keeps hover alive.
    const bridge =
        hoveredNavItem === data.id ? (
            <span
                aria-hidden
                className='absolute left-1/2 top-full z-[55] block h-8 w-56 -translate-x-1/2'
            />
        ) : null

    // Shared trigger (label + chevron) for the dropdown items
    const renderTrigger = () => (
        <div className={`${triggerBase} ${triggerColors} cursor-pointer`}>
            {isActive && isExactRoute() ? (
                <span className='cursor-default'>{data.label}</span>
            ) : (
                <Link
                    href={data.href}
                    onClick={() => setHoveredNavItem('')}
                    className='rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0] focus-visible:ring-offset-2'>
                    {data.label}
                </Link>
            )}
            <FaChevronDown
                className={`h-3 w-3 opacity-70 transition-transform duration-200 ${
                    hoveredNavItem === data.id ? 'rotate-180' : ''
                }`}
            />
        </div>
    )

    return data.type === 'link' && !data.isDropdown ? (
        /* ----------------------------- Plain link ----------------------------- */
        isNavItemActive && isExactRoute() ? (
            <span
                key={data.id}
                className={`inline-flex cursor-default items-center whitespace-nowrap rounded-md px-2.5 py-2 text-sm font-semibold ${
                    overLight ? 'text-[#058BF0]' : 'text-white'
                }`}>
                {data.label}
            </span>
        ) : (
            <Link
                key={data.id}
                href={data.href}
                onClick={() => setHoveredNavItem('')}
                className={`inline-flex items-center whitespace-nowrap rounded-md px-2.5 py-2 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0] focus-visible:ring-offset-2 ${
                    isNavItemActive
                        ? overLight
                            ? 'font-semibold text-[#058BF0]'
                            : 'font-semibold text-white'
                        : overLight
                        ? 'text-[#0a1b3a]/85 hover:bg-[#0a1b3a]/[0.05] hover:text-[#058BF0]'
                        : 'text-white/80 hover:bg-white/[0.07] hover:text-white'
                }`}>
                {data.label}
            </Link>
        )
    ) : data.isDropdown && data.type === 'simple' ? (
        /* ------------------- Simple text dropdown (Company) ------------------- */
        <div
            key={data.id}
            className='relative'
            onMouseEnter={() => setHoveredNavItem(data.id)}
            onMouseLeave={() => setHoveredNavItem('')}>
            {/* Trigger — the label only toggles the menu, it is not a link itself */}
            <div className={`${triggerBase} ${triggerColors} cursor-pointer`}>
                <span>{data.label}</span>
                <FaChevronDown
                    className={`h-3 w-3 opacity-70 transition-transform duration-200 ${
                        hoveredNavItem === data.id ? 'rotate-180' : ''
                    }`}
                />
            </div>
            {/* Hover bridge across the gap to the panel */}
            {hoveredNavItem === data.id && (
                <span aria-hidden className='absolute left-0 top-full z-[55] block h-3 w-48' />
            )}
            <AnimatePresence>
                {hoveredNavItem === data.id && (
                    <motion.div
                        className={`absolute left-0 top-[calc(100%+8px)] z-[60] min-w-[200px] rounded-2xl p-2 ${panelBg}`}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}>
                        <ul className='flex flex-col gap-0.5'>
                            {data.dropdown.map((item) => {
                                const isItemActive = isActiveRoute(item.href)
                                return (
                                    <li key={item.id}>
                                        {isItemActive ? (
                                            <span
                                                className={`block cursor-default rounded-xl px-3 py-2 text-sm font-semibold ${activeItemBg} ${activeHeading}`}>
                                                {item.label}
                                            </span>
                                        ) : (
                                            <Link
                                                href={item.href}
                                                onClick={() => setHoveredNavItem('')}
                                                className={`block rounded-xl px-3 py-2 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0] ${
                                                    overLight
                                                        ? 'text-[#0a1b3a]/80 hover:bg-[#058BF0]/[0.07] hover:text-[#058BF0]'
                                                        : 'text-white/75 hover:bg-white/[0.06] hover:text-white'
                                                }`}>
                                                {item.label}
                                            </Link>
                                        )}
                                    </li>
                                )
                            })}
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    ) : data.isDropdown && data.type === 'link' ? (
        /* ------------------------- Mega-menu dropdown ------------------------- */
        <div
            key={data.id}
            className='relative'
            onMouseEnter={() => setHoveredNavItem(data.id)}
            onMouseLeave={() => setHoveredNavItem('')}>
            {renderTrigger()}
            {bridge}
            <AnimatePresence>
                {hoveredNavItem === data.id && (
                    <div className={`fixed right-1/2 ${dropTop} z-[60] w-[90vw] max-w-[1100px] translate-x-1/2`}>
                        <motion.div
                            className={`flex flex-wrap justify-between rounded-2xl p-3 ${panelBg}`}
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}>
                            {data.dropdown.map((subMenu) => (
                                <div key={subMenu.id} className='min-w-[300px] flex-1 p-2'>
                                    <p
                                        className={`mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] ${sectionLabel}`}>
                                        {subMenu.sub_section_label}
                                    </p>
                                    <ul className='flex flex-col gap-0.5'>
                                        {subMenu.sub_section_items.map((subSectionItem) => {
                                            const isItemActive = isActiveRoute(subSectionItem.href)
                                            return (
                                                <li key={subSectionItem.id} className='group'>
                                                    {isItemActive ? (
                                                        <span
                                                            className={`flex cursor-default items-center gap-3 rounded-xl px-3 py-2.5 ${activeItemBg}`}>
                                                            <span
                                                                className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#058BF0] text-white'>
                                                                {cloneElement(subSectionItem.icon, {
                                                                    className: 'w-4 h-4',
                                                                })}
                                                            </span>
                                                            <span className='min-w-0'>
                                                                <span
                                                                    className={`block text-sm font-semibold ${activeHeading}`}>
                                                                    {subSectionItem.heading ||
                                                                        subSectionItem.label}
                                                                </span>
                                                                {subSectionItem.subheading && (
                                                                    <span
                                                                        className={`block text-xs ${itemSub}`}>
                                                                        {subSectionItem.subheading}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </span>
                                                    ) : (
                                                        <Link
                                                            href={subSectionItem.href}
                                                            onClick={() => setHoveredNavItem('')}
                                                            className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors duration-200 ${itemHover} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0]`}>
                                                            <span
                                                                className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border transition-colors duration-200 ${iconChip}`}>
                                                                {cloneElement(subSectionItem.icon, {
                                                                    className: 'w-4 h-4',
                                                                })}
                                                            </span>
                                                            <span className='min-w-0'>
                                                                <span
                                                                    className={`block text-sm font-semibold transition-colors duration-200 ${itemHeading}`}>
                                                                    {subSectionItem.heading ||
                                                                        subSectionItem.label}
                                                                </span>
                                                                {subSectionItem.subheading && (
                                                                    <span
                                                                        className={`block text-xs transition-colors duration-200 ${itemSub}`}>
                                                                        {subSectionItem.subheading}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </Link>
                                                    )}
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </div>
                            ))}

                            {/* CTA strip */}
                            <div
                                className={`mt-2 flex w-full flex-col items-center justify-between gap-3 rounded-xl px-4 py-3 md:flex-row ${
                                    overLight ? 'bg-[#058BF0]/[0.06]' : 'bg-white/[0.04]'
                                }`}>
                                <p
                                    className={`text-sm ${
                                        overLight ? 'text-[#0a1b3a]' : 'text-white'
                                    }`}>
                                    Looking for something{' '}
                                    <span className='inline-flex items-center gap-1 font-semibold text-[#058BF0]'>
                                        <FaLightbulb className='text-xs text-amber-400' />
                                        unique
                                    </span>
                                    ? Tell us what you need.
                                </p>
                                <Link
                                    href='/contact?ref=navbar'
                                    onClick={() => setHoveredNavItem('')}
                                    className='inline-flex shrink-0 items-center rounded-lg bg-[#058BF0] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-[#058BF0]/25 transition hover:bg-[#0470C4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0] focus-visible:ring-offset-2'>
                                    Contact Us
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    ) : (
        /* -------------------- Products dropdown (image preview) -------------------- */
        <div
            key={data.id}
            className='relative'
            onMouseEnter={() => setHoveredNavItem(data.id)}
            onMouseLeave={() => setHoveredNavItem('')}>
            {renderTrigger()}
            {bridge}
            <AnimatePresence>
                {hoveredNavItem === data.id && (
                    <div className={`fixed right-1/2 ${dropTop} z-[60] w-[76vw] max-w-[920px] translate-x-1/2`}>
                        <motion.div
                            className={`flex gap-5 rounded-2xl p-4 ${panelBg}`}
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}>
                            {/* Left: product links */}
                            <div className='flex min-w-[220px] flex-col gap-3'>
                                <p
                                    className={`px-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${sectionLabel}`}>
                                    {data.dropdown.heading}
                                </p>
                                <div className='flex flex-col gap-0.5'>
                                    {data.dropdown.links.map((link) => {
                                        const isLinkActive = isActiveRoute(link.href)
                                        return isLinkActive ? (
                                            <span
                                                key={link.id}
                                                onMouseEnter={() => setActiveProduct(link.id)}
                                                className='flex cursor-default items-center gap-2.5 rounded-lg bg-[#058BF0] px-3 py-2 text-sm font-semibold text-white'>
                                                <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-white/80' />
                                                {link.label}
                                            </span>
                                        ) : (
                                            <Link
                                                key={link.id}
                                                href={link.href}
                                                target={link.target ? link.target : '_self'}
                                                onMouseEnter={() => setActiveProduct(link.id)}
                                                onClick={() => setHoveredNavItem('')}
                                                className={`group flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0] ${
                                                    overLight
                                                        ? 'text-[#0a1b3a]/75 hover:bg-[#058BF0]/[0.07] hover:text-[#058BF0]'
                                                        : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
                                                }`}>
                                                <span
                                                    className={`h-1.5 w-1.5 shrink-0 rounded-full transition-colors ${
                                                        overLight
                                                            ? 'bg-[#0a1b3a]/20 group-hover:bg-[#058BF0]'
                                                            : 'bg-white/25 group-hover:bg-[#5BB8FF]'
                                                    }`}
                                                />
                                                {link.label}
                                            </Link>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* Right: image preview + description (opacity-only swap = no flicker) */}
                            <div className='flex min-w-0 flex-1 flex-col gap-3'>
                                <div
                                    className={`relative h-[420px] w-full overflow-hidden rounded-xl ${
                                        overLight ? 'bg-[#0a1b3a]/[0.04]' : 'bg-white/[0.04]'
                                    }`}>
                                    {data.dropdown.images.map((image) => (
                                        <motion.div
                                            key={image.id}
                                            animate={{ opacity: activeProduct === image.id ? 1 : 0 }}
                                            transition={{ duration: 0.2, ease: 'easeInOut' }}
                                            className='absolute inset-0'>
                                            <Image
                                                src={image.src}
                                                alt=''
                                                fill
                                                className='rounded-xl object-cover'
                                            />
                                        </motion.div>
                                    ))}
                                </div>
                                <div className='relative h-[52px] overflow-hidden'>
                                    {data.dropdown.images.map((image) => (
                                        <motion.p
                                            key={image.id}
                                            animate={{ opacity: activeProduct === image.id ? 1 : 0 }}
                                            transition={{ duration: 0.2, ease: 'easeInOut' }}
                                            className={`absolute inset-x-0 line-clamp-2 text-center text-sm ${
                                                overLight ? 'text-[#0a1b3a]/60' : 'text-white/55'
                                            }`}>
                                            {image.description}
                                        </motion.p>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default NavItem
